import {useCallback, useEffect, useMemo, useState} from 'react';

import type * as GQL from '../generated/graphql';
import {
  ARTIFACT_CODE_FILE_QUERY,
  AVAILABLE_FILES_QUERY,
} from '../graphql/media';
import {useApolloClient} from '../state/hooks';
import {type RunSignature} from '../types/run';
import {propagateErrorsContext} from './errors';
import {resolveMediaFetchCredentials} from './mediaFetchCredentials';

export interface RetrySpec {
  maxRetries: number;
  calcDelay: (n: number) => number;
}

const STANDARD_RETRY: RetrySpec = {
  maxRetries: 10,
  calcDelay: (n: number) => 500 * Math.pow(1.51337, n),
};

export const NO_RETRY: RetrySpec = {
  maxRetries: 0,
  calcDelay: () => 0,
};

export interface RetryRequest {
  abort: () => void;
}

export function isAbortError(err: unknown): err is DOMException {
  return err instanceof DOMException && err.name === 'AbortError';
}

// When the user navigates away, the browser rejects in-flight fetches with a
// generic `TypeError: Failed to fetch` rather than an AbortError, so without
// this the retry loop below would treat a page unload as a network failure,
// retry into a dying page, and eventually surface `onFailure` (which callers
// log as a user-facing error). Instead, `pagehide`/`freeze` abort every
// in-flight request, and requests started while unloading are parked until
// `pageshow`/`resume` (bfcache restore) so a restored page still loads.
let pageUnloading = false;
const inFlightRequests = new Set<(err: DOMException) => void>();
const parkedRequests = new Set<() => void>();
let lifecycleListenersRegistered = false;

// Registered lazily from `fetchWithRetry` so importing this module has no
// side effects in non-browser environments.
function registerPageLifecycleListeners(): void {
  if (
    lifecycleListenersRegistered ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }
  lifecycleListenersRegistered = true;
  const abortInFlight = () => {
    pageUnloading = true;
    const reason = new DOMException('page unloading', 'AbortError');
    for (const abort of Array.from(inFlightRequests)) {
      abort(reason);
    }
    inFlightRequests.clear();
  };
  const resumeParked = () => {
    pageUnloading = false;
    for (const start of Array.from(parkedRequests)) {
      start();
    }
    parkedRequests.clear();
  };
  window.addEventListener('pagehide', abortInFlight);
  document.addEventListener('freeze', abortInFlight);
  window.addEventListener('pageshow', resumeParked);
  document.addEventListener('resume', resumeParked);
}

// Makes an http request with retries included
// options:
//   onError(e, retry) - called every time a request returns a failure. This
//     allows the caller to check the error and decide to retry or perform
//     a different action
//   onFailure - called if the request fails more than the max limit
//   onSuccess - called when the request returns successful
//   onAbort - called once when the request is abandoned via abort() or page unload; onError/onFailure are not called
//   retry - A spec to control the way the request is retried
//   method - defaults to GET
export function fetchWithRetry(
  url: string,
  options: {
    onError?: () => void;
    onSuccess?: (response: any) => void;
    onFailure?: CallableFunction;
    onAbort?: (err: DOMException) => void;
    retry?: RetrySpec;
    method?: string;
    responseType?: XMLHttpRequestResponseType | 'file';
  }
): RetryRequest {
  registerPageLifecycleListeners();
  const {
    onError,
    onSuccess,
    onFailure,
    onAbort,
    retry = STANDARD_RETRY,
    method,
    responseType,
  } = options;
  const {maxRetries, calcDelay} = retry;

  // Keep track of the latest retry timeout
  let timerID: number;

  // We hold the retry count as a local closure around the event.
  // Doing this instead of holding it in state allows us to create the
  // event listeners once and only recreated them on source change
  let retryCount = 0;
  let controller = new AbortController();
  let cancelled = false;

  const credentials = resolveMediaFetchCredentials(url);

  const makeRequest = async () => {
    if (cancelled) {
      return;
    }
    try {
      // eslint-disable-next-line
      const res = await fetch(url, {
        method: method || 'GET',
        signal: controller.signal,
        ...(credentials != null ? {credentials} : {}),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      let data;
      if (responseType === 'blob') {
        data = await res.blob();
      } else if (responseType === 'file') {
        const blob = await res.blob();
        data = blobToFile(blob, url);
      } else if (responseType === 'arraybuffer') {
        data = await res.arrayBuffer();
      } else if (responseType === 'json') {
        data = await res.json();
      } else {
        //responseType === 'text' || !responseType
        data = await res.text();
      }
      onSuccess?.(data);
      inFlightRequests.delete(settleAborted);
    } catch (e) {
      if (cancelled) {
        return;
      }
      // A failure that lands during unload is the navigation, not the network:
      // settle as aborted rather than retrying or reporting it.
      if (pageUnloading) {
        settleAborted(new DOMException('page unloading', 'AbortError'));
        return;
      }
      if (e instanceof Error && e.name === 'AbortError') {
        return;
      }
      onError?.();
      if (retryCount >= maxRetries) {
        inFlightRequests.delete(settleAborted);
        onFailure?.();
        return;
      }
      const delay = calcDelay(retryCount);
      timerID = window.setTimeout(() => {
        retryCount++;
        controller = new AbortController();
        makeRequest();
      }, delay);
    }
  };

  // Single idempotent cancel path shared by the caller's `abort()` and the
  // page-unload listener, so the retry timer, the AbortController and the
  // in-flight/parked bookkeeping can't drift apart.
  const settleAborted = (err: DOMException) => {
    if (cancelled) {
      return;
    }
    cancelled = true;
    if (timerID) {
      window.clearTimeout(timerID);
    }
    inFlightRequests.delete(settleAborted);
    parkedRequests.delete(makeRequest);
    controller.abort(err);
    onAbort?.(err);
  };

  const abort = () =>
    settleAborted(new DOMException('client abort', 'AbortError'));

  inFlightRequests.add(settleAborted);
  // Callers refetch as soon as an aborted tile is reset, which would fire a
  // new request into the unloading page; hold it until the page is shown.
  if (pageUnloading) {
    parkedRequests.add(makeRequest);
  } else {
    makeRequest();
  }

  return {abort};
}

// Do not interpolate the download URL into this message. These errors are
// console.error'd and can reach Sentry / Datadog; signed-URL query params
// are credentials.
const MEDIA_FETCH_FAILURE_MESSAGE = 'too many request failures for media fetch';

export async function getUrlWithRetry(
  url: string,
  options?: {
    retry?: RetrySpec;
  }
): Promise<string> {
  return new Promise((resolve, reject) => {
    fetchWithRetry(url, {
      retry: options?.retry,
      onSuccess: response => {
        resolve(response);
      },
      onAbort: err => reject(err),
      onFailure: () => {
        reject(MEDIA_FETCH_FAILURE_MESSAGE);
      },
    });
  });
}

export function getBlobWithRetry(
  url: string,
  options?: {
    retry?: RetrySpec;
  }
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    fetchWithRetry(url, {
      retry: options?.retry,
      responseType: 'blob',
      onSuccess: (blob: Blob) => resolve(blob),
      onAbort: err => reject(err),
      onFailure: () => reject(new Error(MEDIA_FETCH_FAILURE_MESSAGE)),
    });
  });
}

interface GetDirectUrlArgs {
  entityName: string;
  projectName: string;
  runName: string;
  filename: string;
}

export type FileInfo = Pick<
  GQL.File,
  'directUrl' | 'id' | 'name' | 'sizeBytes' | 'url'
>;

// Cache the file urls for N
export async function fetchFileInfo(
  client: ReturnType<typeof useApolloClient>,
  args: GetDirectUrlArgs
): Promise<FileInfo | null> {
  const {filename, ...passthroughArgs} = args;
  const variables = {
    includeDirectUrl: true,
    ...passthroughArgs,
    filenames: [filename],
  };

  // google cloud store urls expire in 60 seconds, so we need to disable apollo caching
  const response = await client.query({
    query: AVAILABLE_FILES_QUERY,
    fetchPolicy: 'no-cache',
    variables,
    context: propagateErrorsContext(),
  });

  const fileInfo = response.data?.project?.run?.files?.edges?.[0]?.node ?? null;
  return fileInfo?.directUrl ? fileInfo : null;
}

type DownloadFileFn = (r: RunSignature, f: string) => Promise<void>;

export function useDownloadFile(): DownloadFileFn {
  const client = useApolloClient();
  return useCallback(
    async (runSignature: RunSignature, filename: string) => {
      const fileInfo = await fetchFileInfo(client, {...runSignature, filename});
      if (fileInfo == null) {
        throw new Error(
          `failed to fetch url for file download: ${JSON.stringify({
            ...runSignature,
            filename,
          })}`
        );
      }
      window.open(fileInfo.directUrl);
    },
    [client]
  );
}

// `useLoadFile` is a react hook for loading files from our file store
// It handles the fetching of the signed urls for cloud storage,
// supports fallback for missing files, and request retries for failed
// requests

//
// useLoadFile:
// - options
//   fallback:  A function that is called when the requested
//              resource returns a 404
//   onSuccess: A function that is called when the file is successfully
//              fetched
//   onFailure: A function that is called after all retries have failed
//   retry:     A retry spec to control how retries operate
//   skip:      When true a request will not be made
export function useLoadFile(
  {entityName, projectName, runName}: RunSignature,
  filePath: string,
  options: {
    fallback?: () => void;
    onSuccess?: (response: any, metadata: FileInfo) => void;
    onFailure?: () => void;
    retry?: RetrySpec;
    skip?: boolean;
    responseType?: XMLHttpRequestResponseType | 'file';
  }
) {
  const {onSuccess, onFailure, fallback, retry, responseType, skip} = options;

  const client = useApolloClient();
  const [loading, setLoading] = useState(true);

  // Creat a memoized version of the runSignature, so it will only trigger
  // hook updates when its contents change
  const runSignature = useMemo(
    () => ({
      entityName,
      projectName,
      runName,
    }),
    [entityName, projectName, runName]
  );

  const filename = normalizeMediaFilepath(filePath);

  useEffect(() => {
    if (skip) {
      return;
    }

    let retryReq: RetryRequest | undefined;
    let aborted = false;
    (async () => {
      const fileInfo = await fetchFileInfo(client, {...runSignature, filename});

      if (aborted) {
        return;
      }

      if (!fileInfo || fileInfo.directUrl == null || fileInfo.sizeBytes === 0) {
        const isEmpty = fileInfo?.sizeBytes === 0;
        if (fallback) {
          fallback();
        } else if (isEmpty && onSuccess) {
          // Empty files without fallback are successful with empty content
          onSuccess('', fileInfo!);
        }
        setLoading(false);
        return;
      }

      retryReq = fetchWithRetry(fileInfo.directUrl, {
        responseType,
        retry,
        onSuccess: res => {
          onSuccess?.(res, fileInfo);
          setLoading(false);
        },
        onFailure: () => {
          onFailure?.();
        },
      });
    })();

    return () => {
      aborted = true;
      retryReq?.abort();
    };
  }, [
    client,
    skip,
    runSignature,
    filename,
    fallback,
    onFailure,
    onSuccess,
    responseType,
    retry,
  ]);

  // Indicate loading finished for skipped hooks
  return skip ? false : loading;
}

export async function fetchArtifactFileInfo(
  client: ReturnType<typeof useApolloClient>,
  args: GetDirectUrlArgs
): Promise<FileInfo | null> {
  const {filename, ...passthroughArgs} = args;

  const variables = {
    ...passthroughArgs,
    filenames: [filename],
  };
  // google cloud store urls expire in 60 seconds, so we need to disable apollo caching
  const response = await client.query({
    query: ARTIFACT_CODE_FILE_QUERY,
    fetchPolicy: 'no-cache',
    variables,
    context: propagateErrorsContext(),
  });

  let fileInfo: FileInfo | null = null;
  response?.data?.project?.run?.outputArtifacts?.edges?.forEach(
    (edge: GQL.ArtifactEdge) => {
      // Find the edge that has the file we want...
      edge?.node?.files?.edges?.forEach((fileEdge: GQL.FileEdge) => {
        if (fileEdge?.node?.name === filename) {
          fileInfo = fileEdge.node;
        }
      });
    }
  );

  return fileInfo;
}

// very similar to useLoadFile, but for code artifacts
// TODO: refactor to use a single function
export function useLoadFileInArtifact(
  {entityName, projectName, runName}: RunSignature,
  filePath: string,
  options: {
    fallback?: () => void;
    onSuccess?: (response: any, metadata: FileInfo) => void;
    onFailure?: () => void;
    retry?: RetrySpec;
    skip?: boolean;
    responseType?: XMLHttpRequestResponseType | 'file';
  }
) {
  const {onSuccess, onFailure, fallback, retry, responseType, skip} = options;

  const client = useApolloClient();
  const [loading, setLoading] = useState(true);

  // Creat a memoized version of the runSignature, so it will only trigger
  // hook updates when its contents change
  const runSignature = useMemo(
    () => ({
      entityName,
      projectName,
      runName,
    }),
    [entityName, projectName, runName]
  );

  const filename = normalizeCodeFilepath(filePath);

  useEffect(() => {
    if (skip) {
      return;
    }

    let retryReq: RetryRequest | undefined;
    let aborted = false;
    (async () => {
      const fileInfo = await fetchArtifactFileInfo(client, {
        ...runSignature,
        filename,
      });

      if (aborted) {
        return;
      }

      if (!fileInfo || fileInfo.directUrl == null || fileInfo.sizeBytes === 0) {
        const isEmpty = fileInfo?.sizeBytes === 0;
        if (fallback) {
          fallback();
        } else if (isEmpty && onSuccess) {
          // Empty files without fallback are successful with empty content
          onSuccess('', fileInfo!);
        }
        setLoading(false);
        return;
      }

      retryReq = fetchWithRetry(fileInfo.directUrl, {
        responseType,
        retry,
        onSuccess: res => {
          onSuccess?.(res, fileInfo);
          setLoading(false);
        },
        onFailure: () => {
          onFailure?.();
        },
      });
    })();

    return () => {
      aborted = true;
      retryReq?.abort();
    };
  }, [
    client,
    skip,
    runSignature,
    filename,
    fallback,
    onFailure,
    onSuccess,
    responseType,
    retry,
  ]);

  // Indicate loading finished for skipped hooks
  return skip ? false : loading;
}

// On windows, prior to CLI 0.8.26, we were saving '\' in media paths
// (in media file references that are saved in history or summary). We
// convert them back here. This isn't just a blind replace of all
// backslashes with forward-slashes, because we don't want to do that
// if the path was set on a non-windows machine. We don't have an easy
// way to detect if the data was logged from windows here, so we just
// fix up media paths (which is the only case that needs to be fixed
// anyway).
export function normalizeMediaFilepath(filePath: string): string {
  return filePath.startsWith('media\\')
    ? filePath.replace(/\\/g, '/')
    : filePath;
}

export function normalizeCodeFilepath(filePath: string): string {
  return filePath.startsWith('code/')
    ? filePath.replace(/code\//, '')
    : filePath;
}

export function blobToFile(theBlob: Blob, url: string): File | null {
  try {
    return new File([theBlob], url);
  } catch {
    return null;
  }
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.setAttribute('download', filename);
  a.click();
}
